/**
 * Query keys mirror the endpoint catalog (§12.2), so invalidation is prefix-based:
 * `['profiles']` clears every profiles list, `['view', t, id]` every query of one view.
 */

import type { NotificationFilter, SubjectType } from '@lw/shared';

export const QK = {
  /** `GET /api/auth/me` — boot gate and health probe. */
  auth: ['auth'] as const,

  /** Root of every profiles list (#4). */
  profiles: ['profiles'] as const,
  profilesList: (params?: { includeArchived?: boolean }) =>
    ['profiles', 'list', params ?? {}] as const,

  /** Root of every groups query (#11). */
  groups: ['groups'] as const,
  groupsList: () => ['groups', 'list'] as const,

  /** Scrape history of one profile (#10) — invalidated by `run.updated`. */
  runs: (profileId: number) => ['runs', profileId] as const,
  runsPage: (profileId: number, params?: { offset?: number; limit?: number }) =>
    ['runs', profileId, params ?? {}] as const,
  run: (runId: number) => ['run', runId] as const,

  /** Global status (#18) — invalidated by `status.changed`. */
  status: ['status'] as const,

  /** Settings (#20). */
  settings: ['settings'] as const,

  /** Installed scraper plugins, their config and slot binding (#35). */
  scrapers: ['scrapers'] as const,

  /** Save-to-Notion configuration (#40) — the readiness check a save does before it fires. */
  notion: ['notion'] as const,
  /** The live database picker (#42); only fetched while the Notion tab is open. */
  notionDatabases: ['notion', 'databases'] as const,

  /** Telegram binding state (#52) — polled while a handshake code is outstanding. */
  telegram: ['telegram'] as const,

  /**
   * New Post Watching (#68-#76). The settings-tab state and the `/watch` feed sit on **separate**
   * roots on purpose: a target toggle or a settings save invalidates `watch` constantly, and one
   * shared root would drop every loaded feed page with it.
   */
  watch: ['watch'] as const,
  watchFeed: ['watch-feed'] as const,
  watchFeedPage: (params: unknown) => ['watch-feed', 'page', params] as const,
  /** The watch bot (#73) — polled while its handshake code is outstanding, like #52's. */
  watchTelegram: ['watch', 'telegram'] as const,

  /** Root of all view queries (#23-#27); `status.changed` invalidates the whole subtree. */
  views: ['view'] as const,
  view: (t: SubjectType, id: number) => ['view', t, id] as const,
  viewSummary: (t: SubjectType, id: number) => ['view', t, id, 'summary'] as const,
  viewMetrics: (t: SubjectType, id: number, params: unknown) =>
    ['view', t, id, 'metrics', params] as const,
  viewPosts: (t: SubjectType, id: number, params: unknown) =>
    ['view', t, id, 'posts', params] as const,
  viewFeed: (t: SubjectType, id: number, params: unknown) =>
    ['view', t, id, 'feed', params] as const,
  viewProfile: (t: SubjectType, id: number, params: unknown) =>
    ['view', t, id, 'profile', params] as const,

  /**
   * The global feed (#51). Rooted so the "New posts" pill can invalidate every page of it with one
   * call, whatever scope/filter/sort the screen currently holds.
   */
  feed: ['feed'] as const,
  feedPage: (params: unknown) => ['feed', 'page', params] as const,

  /**
   * Saved posts (#56-#66). Everything hangs off one root, so a save/unsave can refresh the lists,
   * the tag vocabulary, the popup prefill and every page of `/saved` with a single invalidation —
   * while the *feed* pages are deliberately patched in place instead (a feed never reorders itself).
   */
  saved: ['saved'] as const,
  savedLists: () => ['saved', 'lists'] as const,
  savedTags: () => ['saved', 'tags'] as const,
  savedState: (postId: number) => ['saved', 'post', postId] as const,
  savedPage: (params: unknown) => ['saved', 'page', params] as const,

  /** Post preview popup (#28). */
  post: (postId: number) => ['post', postId] as const,
  /** Raw provider item behind a post (#39) — fetched only when the inspector is opened. */
  postRaw: (postId: number) => ['post', postId, 'raw'] as const,

  /** Root of every notification query (#48) — invalidated by `notification.fired`. */
  notifications: ['notifications'] as const,
  notificationsList: (filter: NotificationFilter, limit?: number) =>
    ['notifications', 'list', filter, limit ?? null] as const,

  /** Background jobs (#34) — invalidated by `job.progress`. */
  jobs: ['jobs'] as const,
  job: (jobId: number) => ['jobs', jobId] as const,

  /**
   * Comment generation (#77-#85). Two roots, never one: `ai` is the settings/readiness answer the
   * popup checks before offering a chat (60 s `staleTime`, the `notion` treatment), while each
   * post's chat is its own entry that mutations overwrite in place — a config save must not drop a
   * conversation someone is reading.
   */
  ai: ['ai'] as const,
  aiChat: (postId: number) => ['ai-chat', postId] as const,

  /**
   * Content Performance (#87-#93). One root, because there is no SSE here: an import or a session
   * delete re-resolves the whole merged dataset server-side, so both mutations invalidate
   * `['content']` and every chart, page and session row is re-read together.
   */
  content: ['content'] as const,
  contentMetrics: (params: unknown) => ['content', 'metrics', params] as const,
  contentPosts: (params: unknown) => ['content', 'posts', params] as const,
  contentComments: (params: unknown) => ['content', 'comments', params] as const,
  contentSessions: () => ['content', 'sessions'] as const,

  /** Persisted UI state (#31). */
  uiState: (key: string) => ['ui-state', key] as const,
} as const;
